import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import { api } from "../lib/api";
import { pushSupported, isIOS, isStandalone, currentSubscription, enablePush, disablePush } from "../lib/push";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Bell, BellOff, Send, Smartphone, Wheat, Receipt, CalendarCheck, Wrench, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CATS = [
  { key: "contracts", label: "Contract deliveries", hint: "14 days, 7 days and the day of delivery", icon: Wheat },
  { key: "invoices", label: "Overdue invoices", hint: "Day after due, then weekly until paid", icon: Receipt, ownerOnly: true },
  { key: "fmv", label: "FMV year-end", hint: "Dec 15 and Dec 31", icon: CalendarCheck, ownerOnly: true },
  { key: "maintenance", label: "Maintenance due", hint: "7 days out, day of, and day after if missed", icon: Wrench },
];
const ALL_ON = { contracts: true, invoices: true, fmv: true, maintenance: true };

export default function Notifications() {
  const { user } = useAuth();
  const isOwner = !user || user.role === "owner";
  const cats = CATS.filter((c) => isOwner || !c.ownerOnly);
  const [endpoint, setEndpoint] = useState(null);
  const [prefs, setPrefs] = useState(ALL_ON);
  const [busy, setBusy] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [permission, setPermission] = useState(pushSupported() ? Notification.permission : "unsupported");

  const loadStatus = async () => {
    const sub = await currentSubscription().catch(() => null);
    if (!sub) return setEndpoint(null);
    const { data } = await api.post("/push/status", { endpoint: sub.endpoint });
    setEndpoint(data.subscribed ? sub.endpoint : null);
    setPrefs(data.prefs);
  };
  const loadUpcoming = () =>
    api.get("/push/upcoming").then((r) => setUpcoming(r.data.alerts)).catch(() => setUpcoming([]));

  useEffect(() => { loadStatus(); loadUpcoming(); }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      const res = await enablePush(prefs);
      setEndpoint(res.endpoint);
      setPrefs(res.prefs);
      toast.success("Alerts are on for this device");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not turn on alerts");
    } finally {
      setPermission(pushSupported() ? Notification.permission : "unsupported");
      setBusy(false);
    }
  };
  const turnOff = async () => {
    setBusy(true);
    await disablePush();
    setEndpoint(null);
    setBusy(false);
    toast.success("Alerts turned off for this device");
  };
  const togglePref = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    if (!endpoint) return;
    try { await api.put("/push/prefs", { endpoint, prefs: next }); }
    catch { toast.error("Could not save preference"); setPrefs(prefs); }
  };
  const sendTest = async () => {
    try { await api.post("/push/test"); toast.success("Test alert sent — check your notifications"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  return (
    <div data-testid="notifications-page">
      <TopBar title="Notifications" subtitle="Phone & desktop alerts, checked every morning at 7:00 AM Central." />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <DeviceCard {...{ endpoint, busy, permission, turnOn, turnOff, sendTest }} />
          <div className="bg-white border border-[#1F2A47] p-5" data-testid="push-prefs-card">
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#66645E] mb-4">What to ping me about</h3>
            <div className="divide-y divide-[#E5E2DA]">
              {cats.map((c) => (
                <label key={c.key} className="flex items-center gap-4 py-3 cursor-pointer">
                  <c.icon className="w-5 h-5 text-[#1A4135] shrink-0" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-[#1F1E1B]">{c.label}</span>
                    <span className="block text-xs text-[#66645E]">{c.hint}</span>
                  </span>
                  <Switch data-testid={`push-pref-${c.key}`} checked={!!prefs[c.key]} onCheckedChange={(v) => togglePref(c.key, v)} />
                </label>
              ))}
            </div>
          </div>
        </div>
        <UpcomingList alerts={upcoming.filter((a) => prefs[a.category])} />
      </div>
    </div>
  );
}

function DeviceCard({ endpoint, busy, permission, turnOn, turnOff, sendTest }) {
  const needsInstall = isIOS() && !isStandalone();
  const on = !!endpoint;
  return (
    <div className="bg-white border border-[#1F2A47] p-5" data-testid="push-device-card">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${on ? "bg-[#1A4135] text-white" : "bg-[#F1EEE6] text-[#66645E]"}`}>
          {on ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-[#1F1E1B]" data-testid="push-status-text">
            {on ? "Alerts are ON for this device" : "Alerts are off for this device"}
          </p>
          <p className="text-xs text-[#66645E] mt-1">Each phone or computer is turned on separately.</p>
        </div>
      </div>
      {needsInstall && (
        <p className="mt-4 text-xs bg-[#FFF6E0] border border-[#B87A00] p-3 flex gap-2" data-testid="push-ios-hint">
          <Smartphone className="w-4 h-4 shrink-0 text-[#B87A00]" />
          On iPhone: tap Share → Add to Home Screen, open AcreLatch from the icon, then turn alerts on here.
        </p>
      )}
      {permission === "unsupported" && !needsInstall && (
        <p className="mt-4 text-xs text-[#BC4727]" data-testid="push-unsupported">This browser doesn't support push alerts.</p>
      )}
      {permission === "denied" && (
        <p className="mt-4 text-xs text-[#BC4727]" data-testid="push-denied">Notifications are blocked — allow them in this browser's site settings.</p>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        {on ? (
          <>
            <button data-testid="push-test-btn" onClick={sendTest} className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#15372C] inline-flex items-center gap-2">
              <Send className="w-4 h-4" /> Send test
            </button>
            <button data-testid="push-disable-btn" onClick={turnOff} disabled={busy} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#F1EEE6] disabled:opacity-40">
              Turn off
            </button>
          </>
        ) : (
          <button data-testid="push-enable-btn" onClick={turnOn} disabled={busy || permission === "unsupported" || needsInstall} className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#15372C] inline-flex items-center gap-2 disabled:opacity-40">
            <Bell className="w-4 h-4" /> {busy ? "Turning on…" : "Turn on alerts"}
          </button>
        )}
      </div>
    </div>
  );
}

function UpcomingList({ alerts }) {
  return (
    <div className="lg:col-span-2 bg-white border border-[#1F2A47] p-5 self-start" data-testid="push-upcoming-card">
      <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#66645E] mb-4 flex items-center gap-2">
        <Clock className="w-4 h-4" /> Coming up · next 15 days
      </h3>
      {alerts.length === 0 ? (
        <p className="text-sm text-[#66645E]" data-testid="push-upcoming-empty">Nothing scheduled. Add a delivery due date to a contract or a due date to a maintenance task.</p>
      ) : (
        <ul className="space-y-3">
          {alerts.map((a) => (
            <li key={`${a.send_date}-${a.key}`} className="border-l-2 border-[#1A4135] pl-3" data-testid={`push-upcoming-${a.key}`}>
              <span className="block text-[11px] font-bold uppercase tracking-wider text-[#66645E]">{a.send_date} · 7:00 AM</span>
              <span className="block text-sm font-semibold text-[#1F1E1B]">{a.title}</span>
              <span className="block text-xs text-[#66645E]">{a.body}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
